import { RefObject, useRef } from "react";
import { RouteResultForMapView } from "../types/routing";
import StartGoalSpeechBubble from "@/components/StartGoalSpeechBubble";
import SignageSpeechBubble from "@/components/SignageSpeechBubble";
import { createRoot, type Root } from "react-dom/client";
import * as maplibregl from "maplibre-gl";

export const useStartMarkers = (
  map: RefObject<maplibregl.Map | null>,
  isMobile: boolean = false,
) => {
  const startMarkerRef = useRef<maplibregl.Marker | null>(null);
  // unmount しないと i18n の購読などが残り、経路を出すたびに root ごとリークする
  const startRootRef = useRef<Root | null>(null);

  const displayStartMarkers = (floorId: string, route: RouteResultForMapView): void => {
    if (!map.current || !route.nodes || route.nodes.length === 0) return;

    clearStartMarkers();

    const startNode = route.nodes[0];

    if (!startNode) return;

    // スタートマーカーを作成（SP は正面玄関バブル、PC は現在地バブル）
    const startContainer = document.createElement("div");
    const startRoot = createRoot(startContainer);
    startRootRef.current = startRoot;
    startRoot.render(
      isMobile ? <SignageSpeechBubble /> : <StartGoalSpeechBubble variant="start" />,
    );

    startMarkerRef.current = new maplibregl.Marker({
      element: startContainer,
      anchor: "bottom",
    })
      .setLngLat([startNode.position.x, startNode.position.y])
      .addTo(map.current);

    startMarkerRef.current.getElement().dataset.floorId = startNode.floorId;

    // 現在のフロアに応じて表示/非表示を更新
    updateStartMarkersForFloor(floorId);
  };

  const updateStartMarkersForFloor = (floorId: string) => {
    if (!map.current) return;

    // スタートマーカーの表示/非表示
    if (startMarkerRef.current) {
      if (startMarkerRef.current.getElement().dataset.floorId === floorId) {
        startMarkerRef.current.addTo(map.current);
      } else {
        startMarkerRef.current.remove();
      }
    }
  };

  const clearStartMarkers = () => {
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
      startMarkerRef.current = null;
    }
    if (startRootRef.current) {
      const rootToUnmount = startRootRef.current;
      startRootRef.current = null;
      // 描画中に同期 unmount すると React が警告するため次のタスクで行う
      setTimeout(() => rootToUnmount.unmount(), 0);
    }
  };

  return {
    displayStartMarkers,
    clearStartMarkers,
    updateStartMarkersForFloor,
  };
};
